import { RHFStringInput } from "@impero-com/common/components/form/rhfFields/rhfStringInput";
import { assertNever } from "@impero-com/common/utils/obj";
import { Button, FormLabel, CollapseContentSection } from "@impero-com/ui";
import { InputNumber, Typography } from "antd";
import {
    Control,
    Controller,
    useController,
    useFieldArray,
    useWatch,
} from "react-hook-form";
import { z } from "zod";

import { FieldError } from "utils/form/fieldError";

import { CopyableTag } from "./components/copyableTag";
import { HeaderButtons } from "./components/headerButtons";
import { RangeMin, numberOrInfinitySchema } from "./components/rangeMin";
import { FieldKey } from "./fields";
import { newDefaultOption } from "./formData";
import { FormSchema } from "./schema";
import * as theme from "./theme.css";

export const optionValueSchema = z.object({
    optionId: z.guid(),
    value: z.string().trim().min(1),
    weight: z.number(),
    rangeMin: numberOrInfinitySchema,
});
export type RDStructureOption = z.infer<typeof optionValueSchema>;

interface StructureOptionProps {
    index: number;
    option: RDStructureOption;
    fieldKey: FieldKey;
    control: Control<FormSchema>;
}

export function StructureOption({
    index,
    option,
    fieldKey,
    control,
}: StructureOptionProps) {
    return (
        <div className={theme.sectionContent}>
            <span className={theme.fieldIdTag}>
                <Typography.Text>ID:</Typography.Text>
                <CopyableTag value={"$" + option.optionId} />
            </span>

            <RHFStringInput
                control={control}
                name={`${fieldKey}.fieldType.options.${index}.value`}
                label="Option Label"
                schema={optionValueSchema.shape.value}
            />
            <FormLabel>Weight</FormLabel>
            <Controller
                control={control}
                name={`${fieldKey}.fieldType.options.${index}.weight`}
                render={({ field }) => {
                    return (
                        <InputNumber
                            placeholder="Weight"
                            onChange={field.onChange}
                            value={field.value}
                        />
                    );
                }}
            />
            <FormLabel>Range Min</FormLabel>
            <RangeMin
                control={control}
                name={`${fieldKey}.fieldType.options.${index}.rangeMin`}
            />
        </div>
    );
}

export type FieldOptionsMode = "Normal" | "ResponseOptions";

export interface FieldOptionsProps {
    control: Control<FormSchema>;
    fieldKey: FieldKey;
    mode: FieldOptionsMode;
}

export function FieldOptions({ control, fieldKey, mode }: FieldOptionsProps) {
    const { fields, append, move, remove } = useFieldArray({
        control,
        name: `${fieldKey}.fieldType.options`,
    });

    const { fieldState } = useController({
        name: `${fieldKey}.fieldType.options`,
        control,
    });

    const addNewField = () => {
        append(newDefaultOption());
    };

    function modeToHeaderButtonsDisabled(mode: FieldOptionsMode): boolean {
        switch (mode) {
            case "Normal": {
                return false;
            }
            case "ResponseOptions": {
                return true;
            }
            default: {
                return assertNever(mode);
            }
        }
    }
    const headerButtonsDisabled = modeToHeaderButtonsDisabled(mode);

    return (
        <>
            <FormLabel>Options</FormLabel>
            {fields.map((option, optionIndex) => {
                return (
                    <FieldOption
                        key={option.id}
                        control={control}
                        fieldKey={fieldKey}
                        index={optionIndex}
                        siblingsLength={fields.length}
                        headerButtonsDisabled={headerButtonsDisabled}
                        onClickRemove={(index) => {
                            remove(index);
                        }}
                        onChangeOrder={(index, newOrder) => {
                            move(index, newOrder);
                        }}
                    />
                );
            })}
            <FieldError
                error={
                    fieldState.error?.message ?? fieldState.error?.root?.message
                }
            />
            <Button onClick={addNewField}>+ Add value</Button>
        </>
    );
}

export interface FieldOptionProps {
    control: Control<FormSchema>;
    fieldKey: FieldKey;
    index: number;
    siblingsLength: number;
    headerButtonsDisabled: boolean;
    onClickRemove: (index: number) => void;
    onChangeOrder: (index: number, newOrder: number) => void;
}

function FieldOption({
    control,
    fieldKey,
    index,
    siblingsLength,
    headerButtonsDisabled,
    onClickRemove,
    onChangeOrder,
}: FieldOptionProps) {
    const option = useWatch({
        name: `${fieldKey}.fieldType.options.${index}`,
        control,
    });
    return (
        <CollapseContentSection
            titleLabel={option.value}
            styleVariant="secondary"
            level={5}
            initialCollapsed={true}
            headerRightChildren={
                <HeaderButtons
                    onClickRemove={onClickRemove}
                    onChangeOrder={onChangeOrder}
                    index={index}
                    siblingsLength={siblingsLength}
                    deleteDisabled={headerButtonsDisabled}
                    changeOrderDisabled={headerButtonsDisabled}
                />
            }
        >
            <StructureOption
                index={index}
                option={option}
                control={control}
                fieldKey={fieldKey}
            />
        </CollapseContentSection>
    );
}
